import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  DollarSign,
  Shield,
  GraduationCap,
  FileText,
  Edit
} from 'lucide-react';
import userService from '../services/userService';
import { useNotification } from '../hooks/useNotification';
import { useAuth } from '../context/AuthContext';
import { ROLE_LABELS, ROLES } from '../config/permissions';
import Modal, { ModalFormGrid, ModalFormColumn, FormGroup } from '../components/common/Modal';
import CustomSelect from '../components/common/CustomSelect';
import '../styles/ClientProfile.css';
import '../styles/FormSections.css';

const TABS = [
  { id: 'personal', label: 'Datos Personales', icon: User },
  { id: 'contact', label: 'Contacto', icon: Phone },
  { id: 'address', label: 'Dirección', icon: MapPin },
  { id: 'employment', label: 'Laboral', icon: Briefcase },
  { id: 'compensation', label: 'Compensación', icon: DollarSign },
  { id: 'benefits', label: 'Seguros y Beneficios', icon: Shield },
  { id: 'education', label: 'Formación', icon: GraduationCap },
  { id: 'experience', label: 'Experiencia', icon: FileText },
  { id: 'notes', label: 'Notas', icon: FileText }
];

const UserProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user: currentUser } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSection, setEditSection] = useState('personal');
  const [formData, setFormData] = useState({});

  const canEdit = currentUser?.role === ROLES.SUPER_ADMIN || currentUser?.role === ROLES.GERENTE;
  const canEditThisUser = canEdit && user && (currentUser?.role === ROLES.SUPER_ADMIN || user.role !== ROLES.SUPER_ADMIN);

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      const data = await userService.getUserById(id);
      setUser(data);
    } catch (error) {
      showNotification('Error al cargar el perfil del usuario', 'error');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showNotification]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const fullName = user ? `${user.personal?.firstName || ''} ${user.personal?.lastName || ''}`.trim() || user.email : '';
  const initials = fullName ? (user.personal?.firstName?.[0] || '') + (user.personal?.lastName?.[0] || '') : (user?.email?.[0] || '?');

  const openEditModal = (section) => {
    setEditSection(section);
    const sectionData = user?.[section];
    setFormData(sectionData ? { ...sectionData } : {});
    setShowEditModal(true);
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await userService.updateUser(id, { [editSection]: formData });
      showNotification('Perfil actualizado', 'success');
      setShowEditModal(false);
      loadUser();
    } catch (error) {
      showNotification(error.message || 'Error al actualizar', 'error');
    }
  };

  if (loading) {
    return (
      <div className="client-profile-loading">
        <div className="loading-spinner"></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="client-profile-page">
      <div className="profile-header">
        <div className="header-content">
          <button className="btn btn-secondary btn-icon" onClick={() => navigate('/users')}>
            <ArrowLeft size={20} />
            Volver
          </button>
        </div>
      </div>

      <div className="client-header">
        <div className="client-info">
          <div className="client-avatar">
            <div className={`avatar-circle ${user.personal?.photo ? 'has-image' : ''}`} style={user.personal?.photo ? { backgroundImage: `url(${user.personal.photo})` } : { fontSize: '28px' }}>
              {!user.personal?.photo && initials.toUpperCase()}
            </div>
          </div>
          <div className="client-details">
            <h1 className="client-name">{fullName || user.email}</h1>
            <p className="client-email">{user.email}</p>
            <div className="client-meta">
              <span className={`badge ${user.isActive ? 'badge-success' : 'badge-secondary'}`}>
                {user.isActive ? 'Activo' : 'Inactivo'}
              </span>
              <span className="badge badge-info">{user.roleLabel || ROLE_LABELS[user.role]}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`tab ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      <div className="profile-content">
        {activeTab === 'personal' && (
          <div className="info-card">
            <div className="card-header">
              <h3><User size={20} /> Datos Personales</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => openEditModal('personal')}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Nombre:</span> {user.personal?.firstName || '-'}</div>
              <div className="info-row"><span className="label">Apellido:</span> {user.personal?.lastName || '-'}</div>
              <div className="info-row"><span className="label">Tipo Doc:</span> {user.personal?.documentType || '-'}</div>
              <div className="info-row"><span className="label">Número Doc:</span> {user.personal?.documentNumber || '-'}</div>
              <div className="info-row"><span className="label">Fecha Nac:</span> {user.personal?.birthDate ? new Date(user.personal.birthDate).toLocaleDateString() : '-'}</div>
              <div className="info-row"><span className="label">Género:</span> {user.personal?.gender === 'M' ? 'Masculino' : user.personal?.gender === 'F' ? 'Femenino' : user.personal?.gender || '-'}</div>
              <div className="info-row"><span className="label">Estado Civil:</span> {user.personal?.maritalStatus || '-'}</div>
              <div className="info-row"><span className="label">Nacionalidad:</span> {user.personal?.nationality || '-'}</div>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="info-card">
            <div className="card-header">
              <h3><Phone size={20} /> Contacto</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => openEditModal('contact')}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Teléfono:</span> {user.contact?.phone || '-'}</div>
              <div className="info-row"><span className="label">Celular:</span> {user.contact?.mobile || '-'}</div>
              <div className="info-row"><span className="label">Contacto Emergencia:</span> {user.contact?.emergencyContact || '-'}</div>
              <div className="info-row"><span className="label">Tel. Emergencia:</span> {user.contact?.emergencyPhone || '-'}</div>
            </div>
          </div>
        )}

        {activeTab === 'address' && (
          <div className="info-card">
            <div className="card-header">
              <h3><MapPin size={20} /> Dirección</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => openEditModal('address')}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Calle:</span> {user.address?.street || '-'}</div>
              <div className="info-row"><span className="label">Ciudad:</span> {user.address?.city || '-'}</div>
              <div className="info-row"><span className="label">Departamento/Estado:</span> {user.address?.state || '-'}</div>
              <div className="info-row"><span className="label">Código Postal:</span> {user.address?.zipCode || '-'}</div>
              <div className="info-row"><span className="label">País:</span> {user.address?.country || '-'}</div>
            </div>
          </div>
        )}

        {activeTab === 'employment' && (
          <div className="info-card">
            <div className="card-header">
              <h3><Briefcase size={20} /> Datos Laborales</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => openEditModal('employment')}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Código Empleado:</span> {user.employment?.employeeId || '-'}</div>
              <div className="info-row"><span className="label">Departamento:</span> {user.employment?.department || '-'}</div>
              <div className="info-row"><span className="label">Cargo:</span> {user.employment?.position || '-'}</div>
              <div className="info-row"><span className="label">Fecha Ingreso:</span> {user.employment?.hireDate ? new Date(user.employment.hireDate).toLocaleDateString() : '-'}</div>
              <div className="info-row"><span className="label">Tipo Contrato:</span> {user.employment?.contractType || '-'}</div>
              <div className="info-row"><span className="label">Horario:</span> {user.employment?.workSchedule || '-'}</div>
            </div>
          </div>
        )}

        {activeTab === 'compensation' && (
          <div className="info-card">
            <div className="card-header">
              <h3><DollarSign size={20} /> Compensación y Salarios</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => openEditModal('compensation')}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Salario Base:</span> {user.compensation?.baseSalary ? new Intl.NumberFormat('es-CO').format(user.compensation.baseSalary) : '-'} {user.compensation?.salaryCurrency || ''}</div>
              <div className="info-row"><span className="label">Método Pago:</span> {user.compensation?.paymentMethod || '-'}</div>
              <div className="info-row"><span className="label">Banco:</span> {user.compensation?.bankName || '-'}</div>
              <div className="info-row"><span className="label">Cuenta:</span> {user.compensation?.bankAccount || '-'}</div>
              <div className="info-row"><span className="label">Tipo Cuenta:</span> {user.compensation?.accountType || '-'}</div>
            </div>
          </div>
        )}

        {activeTab === 'benefits' && (
          <div className="info-card">
            <div className="card-header">
              <h3><Shield size={20} /> Seguros y Beneficios</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => openEditModal('benefits')}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">EPS/Salud:</span> {user.benefits?.healthInsurance || '-'}</div>
              <div className="info-row"><span className="label">Nº Póliza Salud:</span> {user.benefits?.healthPolicyNumber || '-'}</div>
              <div className="info-row"><span className="label">Fondo Pensiones:</span> {user.benefits?.pensionFund || '-'}</div>
              <div className="info-row"><span className="label">ID Pensión:</span> {user.benefits?.pensionId || '-'}</div>
              {user.benefits?.additionalBenefits?.length > 0 && (
                <div className="info-row"><span className="label">Otros:</span> {user.benefits.additionalBenefits.join(', ')}</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'education' && (
          <div className="info-card">
            <div className="card-header">
              <h3><GraduationCap size={20} /> Formación Académica</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => { setFormData(user.education ? [...user.education] : []); setEditSection('education'); setShowEditModal(true); }}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              {user.education?.length > 0 ? (
                user.education.map((edu, i) => (
                  <div key={i} className="info-block">
                    <div className="info-row"><strong>{edu.institution}</strong></div>
                    <div className="info-row">{edu.degree} - {edu.field}</div>
                    <div className="info-row text-sm text-secondary">
                      {edu.startDate ? new Date(edu.startDate).toLocaleDateString() : ''} - {edu.endDate ? new Date(edu.endDate).toLocaleDateString() : 'Actual'}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-secondary">No hay formación registrada</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'experience' && (
          <div className="info-card">
            <div className="card-header">
              <h3><FileText size={20} /> Experiencia Laboral</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => { setFormData(user.workExperience ? [...user.workExperience] : []); setEditSection('workExperience'); setShowEditModal(true); }}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              {user.workExperience?.length > 0 ? (
                user.workExperience.map((exp, i) => (
                  <div key={i} className="info-block">
                    <div className="info-row"><strong>{exp.company}</strong> - {exp.position}</div>
                    <div className="info-row text-sm text-secondary">
                      {exp.startDate ? new Date(exp.startDate).toLocaleDateString() : ''} - {exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'Actual'}
                    </div>
                    {exp.description && <div className="info-row text-sm">{exp.description}</div>}
                  </div>
                ))
              ) : (
                <p className="text-secondary">No hay experiencia registrada</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="info-card">
            <div className="card-header">
              <h3><FileText size={20} /> Notas y Observaciones</h3>
              {canEditThisUser && (
                <button className="btn btn-sm btn-outline" onClick={() => { setFormData({ notes: user.notes || '' }); setEditSection('notes'); setShowEditModal(true); }}>
                  <Edit size={14} />
                </button>
              )}
            </div>
            <div className="card-content">
              <p className="whitespace-pre-wrap">{user.notes || 'Sin notas'}</p>
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title={`Editar ${TABS.find(t => t.id === editSection)?.label || editSection}`}
          onSubmit={handleEditSubmit}
        >
          {editSection === 'notes' && (
            <FormGroup label="Notas">
              <textarea
                value={formData.notes || ''}
                onChange={(e) => updateFormField('notes', e.target.value)}
                rows={5}
                className="form-input form-textarea"
              />
            </FormGroup>
          )}
          {editSection === 'personal' && (
            <ModalFormGrid>
              <ModalFormColumn>
                <FormGroup label="Nombre"><input value={formData.firstName || ''} onChange={(e) => updateFormField('firstName', e.target.value)} className="form-input" /></FormGroup>
                <FormGroup label="Apellido"><input value={formData.lastName || ''} onChange={(e) => updateFormField('lastName', e.target.value)} className="form-input" /></FormGroup>
                <FormGroup label="Tipo Documento">
                  <CustomSelect options={[{ value: 'CC', label: 'Cédula' }, { value: 'CE', label: 'Cédula Extranjería' }, { value: 'PA', label: 'Pasaporte' }]} value={formData.documentType || ''} onChange={(v) => updateFormField('documentType', v)} />
                </FormGroup>
                <FormGroup label="Número Documento"><input value={formData.documentNumber || ''} onChange={(e) => updateFormField('documentNumber', e.target.value)} className="form-input" /></FormGroup>
                <FormGroup label="Fecha Nacimiento"><input type="date" value={formData.birthDate ? formData.birthDate.split('T')[0] : ''} onChange={(e) => updateFormField('birthDate', e.target.value)} className="form-input" /></FormGroup>
              </ModalFormColumn>
              <ModalFormColumn>
                <FormGroup label="Género">
                  <CustomSelect options={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Femenino' }, { value: 'O', label: 'Otro' }]} value={formData.gender || ''} onChange={(v) => updateFormField('gender', v)} />
                </FormGroup>
                <FormGroup label="Estado Civil">
                  <CustomSelect options={[{ value: 'single', label: 'Soltero' }, { value: 'married', label: 'Casado' }, { value: 'divorced', label: 'Divorciado' }, { value: 'widowed', label: 'Viudo' }]} value={formData.maritalStatus || ''} onChange={(v) => updateFormField('maritalStatus', v)} />
                </FormGroup>
                <FormGroup label="Nacionalidad"><input value={formData.nationality || ''} onChange={(e) => updateFormField('nationality', e.target.value)} className="form-input" /></FormGroup>
              </ModalFormColumn>
            </ModalFormGrid>
          )}
          {editSection === 'contact' && (
            <ModalFormGrid>
              <FormGroup label="Teléfono"><input value={formData.phone || ''} onChange={(e) => updateFormField('phone', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Celular"><input value={formData.mobile || ''} onChange={(e) => updateFormField('mobile', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Contacto Emergencia"><input value={formData.emergencyContact || ''} onChange={(e) => updateFormField('emergencyContact', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Tel. Emergencia"><input value={formData.emergencyPhone || ''} onChange={(e) => updateFormField('emergencyPhone', e.target.value)} className="form-input" /></FormGroup>
            </ModalFormGrid>
          )}
          {editSection === 'address' && (
            <ModalFormGrid>
              <FormGroup label="Calle"><input value={formData.street || ''} onChange={(e) => updateFormField('street', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Ciudad"><input value={formData.city || ''} onChange={(e) => updateFormField('city', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Departamento/Estado"><input value={formData.state || ''} onChange={(e) => updateFormField('state', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Código Postal"><input value={formData.zipCode || ''} onChange={(e) => updateFormField('zipCode', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="País"><input value={formData.country || ''} onChange={(e) => updateFormField('country', e.target.value)} className="form-input" /></FormGroup>
            </ModalFormGrid>
          )}
          {editSection === 'employment' && (
            <ModalFormGrid>
              <FormGroup label="Código Empleado"><input value={formData.employeeId || ''} onChange={(e) => updateFormField('employeeId', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Departamento"><input value={formData.department || ''} onChange={(e) => updateFormField('department', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Cargo"><input value={formData.position || ''} onChange={(e) => updateFormField('position', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Fecha Ingreso"><input type="date" value={formData.hireDate ? formData.hireDate.split('T')[0] : ''} onChange={(e) => updateFormField('hireDate', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Tipo Contrato">
                <CustomSelect options={[{ value: 'indefinido', label: 'Indefinido' }, { value: 'fijo', label: 'Término Fijo' }, { value: 'prestacion', label: 'Prestación de Servicios' }, { value: 'aprendizaje', label: 'Aprendizaje' }]} value={formData.contractType || ''} onChange={(v) => updateFormField('contractType', v)} />
              </FormGroup>
              <FormGroup label="Horario"><input value={formData.workSchedule || ''} onChange={(e) => updateFormField('workSchedule', e.target.value)} className="form-input" placeholder="Ej: L-V 8am-5pm" /></FormGroup>
            </ModalFormGrid>
          )}
          {editSection === 'compensation' && (
            <ModalFormGrid>
              <FormGroup label="Salario Base"><input type="number" value={formData.baseSalary ?? ''} onChange={(e) => updateFormField('baseSalary', e.target.value === '' ? 0 : Number(e.target.value))} className="form-input" /></FormGroup>
              <FormGroup label="Moneda"><input value={formData.salaryCurrency || 'COP'} onChange={(e) => updateFormField('salaryCurrency', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Método Pago">
                <CustomSelect options={[{ value: 'transfer', label: 'Transferencia' }, { value: 'cash', label: 'Efectivo' }, { value: 'check', label: 'Cheque' }]} value={formData.paymentMethod || ''} onChange={(v) => updateFormField('paymentMethod', v)} />
              </FormGroup>
              <FormGroup label="Banco"><input value={formData.bankName || ''} onChange={(e) => updateFormField('bankName', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Nº Cuenta"><input value={formData.bankAccount || ''} onChange={(e) => updateFormField('bankAccount', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Tipo Cuenta">
                <CustomSelect options={[{ value: 'savings', label: 'Ahorros' }, { value: 'checking', label: 'Corriente' }]} value={formData.accountType || ''} onChange={(v) => updateFormField('accountType', v)} />
              </FormGroup>
            </ModalFormGrid>
          )}
          {editSection === 'benefits' && (
            <ModalFormGrid>
              <FormGroup label="EPS/Salud"><input value={formData.healthInsurance || ''} onChange={(e) => updateFormField('healthInsurance', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Nº Póliza Salud"><input value={formData.healthPolicyNumber || ''} onChange={(e) => updateFormField('healthPolicyNumber', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="Fondo Pensiones"><input value={formData.pensionFund || ''} onChange={(e) => updateFormField('pensionFund', e.target.value)} className="form-input" /></FormGroup>
              <FormGroup label="ID Pensión"><input value={formData.pensionId || ''} onChange={(e) => updateFormField('pensionId', e.target.value)} className="form-input" /></FormGroup>
            </ModalFormGrid>
          )}
          {(editSection === 'education' || editSection === 'workExperience') && (
            <p className="text-secondary py-4">La edición de formación y experiencia se ampliará en una próxima versión. Por ahora puedes editar estos datos desde el backend.</p>
          )}
        </Modal>
      )}
    </div>
  );
};

export default UserProfilePage;
